import React, { useState, useEffect } from 'react';
import { useAuth } from './contexts/AuthContext';
import { notificationService } from './NotificationService';
import {
    Bell,
    BellRing,
    X,
    Check,
    MessageSquare,
    FileText,
    Megaphone,
    Clock,
    User,
    ChevronDown,
    ChevronUp
} from 'lucide-react';

const NotificationCenter = ({ onNavigateToAssignment }) => {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [expandedNotifications, setExpandedNotifications] = useState(new Set());

    useEffect(() => {
        if (!user) return;

        // Setting up notification listener for user

        const unsubscribe = notificationService.listenToNotifications(
            user.uid,
            (notificationData) => {
                setNotifications(notificationData.all);
                setUnreadCount(notificationData.unreadCount);
                setIsLoading(false);
            }
        );

        return () => {
            unsubscribe();
        };
    }, [user]);

    const handleMarkAsRead = async (notificationId) => {
        try {
            await notificationService.markAsRead(user.uid, notificationId);
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await notificationService.markAllAsRead(user.uid);
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    };

    const toggleExpanded = (notificationId) => {
        const newExpanded = new Set(expandedNotifications);
        if (newExpanded.has(notificationId)) {
            newExpanded.delete(notificationId);
        } else {
            newExpanded.add(notificationId);
        }
        setExpandedNotifications(newExpanded);
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'announcement':
                return <Megaphone className="text-blue-500" size={20} />;
            case 'assignment':
                return <FileText className="text-orange-500" size={20} />;
            case 'chat':
                return <MessageSquare className="text-green-500" size={20} />;
            default:
                return <Bell className="text-gray-500" size={20} />;
        }
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';

        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const now = new Date();
        const diff = now - date;

        if (diff < 60000) return 'Baru saja';
        if (diff < 3600000) return `${Math.floor(diff / 60000)} menit lalu`;
        if (diff < 86400000) return `${Math.floor(diff / 3600000)} jam lalu`;
        return date.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    };

    const handleViewAssignment = async (notification) => {
        try {
            // Mark notification as read
            await notificationService.markAsRead(user.uid, notification.id);

            // Close notification panel
            setIsOpen(false);

            // Navigate to assignment based on type
            if (notification.data?.assignmentType === 'soal') {
                // Navigate to soal ulangan page
                if (onNavigateToAssignment) {
                    onNavigateToAssignment('siswa_soal', {
                        selectedSoalId: notification.data.assignmentId
                    });
                }
            } else if (notification.data?.assignmentType === 'lkpd') {
                // Navigate to LKPD page
                if (onNavigateToAssignment) {
                    onNavigateToAssignment('siswa_lkpd', {
                        selectedLkpdId: notification.data.assignmentId
                    });
                }
            } else {
                // Default: go to dashboard to see all assignments
                if (onNavigateToAssignment) {
                    onNavigateToAssignment('siswa_dashboard');
                }
            }
        } catch (error) {
            console.error('Error handling assignment view:', error);
        }
    };

    return (
        <div className="relative">
            {/* Notification Bell Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 text-gray-600 hover:text-blue-600 transition-colors"
            >
                {unreadCount > 0 ? (
                    <BellRing className="animate-pulse text-blue-600" size={24} />
                ) : (
                    <Bell size={24} />
                )}

                {/* Unread Count Badge */}
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Notification Panel */}
            {isOpen && (
                <div className="absolute top-full right-0 mt-2 w-[85vw] sm:w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-hidden">
                    {/* Header */}
                    <div className="p-4 border-b border-gray-200 bg-gray-50">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-800">
                                Notifikasi
                            </h3>
                            <div className="flex items-center gap-2">
                                {unreadCount > 0 && (
                                    <button
                                        onClick={handleMarkAllAsRead}
                                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                    >
                                        Tandai semua dibaca
                                    </button>
                                )}
                                <button
                                    onClick={() => setIsOpen(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>
                        {unreadCount > 0 && (
                            <p className="text-sm text-gray-600 mt-1">
                                {unreadCount} notifikasi belum dibaca
                            </p>
                        )}
                    </div>

                    {/* Notifications List */}
                    <div className="max-h-80 overflow-y-auto">
                        {isLoading ? (
                            <div className="p-4 text-center text-gray-500">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                                Memuat notifikasi...
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                                <Bell className="mx-auto mb-2 text-gray-300" size={32} />
                                <p>Tidak ada notifikasi</p>
                            </div>
                        ) : (
                            notifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    className={`p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors ${!notification.isRead ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                                        }`}
                                >
                                    <div className="flex items-start gap-3">
                                        {/* Icon */}
                                        <div className="flex-shrink-0 mt-1">
                                            {getNotificationIcon(notification.type)}
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <h4 className="text-sm font-medium text-gray-900 truncate">
                                                        {notification.title}
                                                    </h4>
                                                    <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                                        <User size={12} />
                                                        {notification.senderName}
                                                        <span className="mx-1">•</span>
                                                        <Clock size={12} />
                                                        {formatTime(notification.createdAt)}
                                                    </p>
                                                </div>

                                                {/* Actions */}
                                                <div className="flex items-center gap-1 ml-2">
                                                    {!notification.isRead && (
                                                        <button
                                                            onClick={() => handleMarkAsRead(notification.id)}
                                                            className="text-blue-600 hover:text-blue-800"
                                                            title="Tandai dibaca"
                                                        >
                                                            <Check size={16} />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => toggleExpanded(notification.id)}
                                                        className="text-gray-400 hover:text-gray-600"
                                                    >
                                                        {expandedNotifications.has(notification.id) ? (
                                                            <ChevronUp size={16} />
                                                        ) : (
                                                            <ChevronDown size={16} />
                                                        )}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Message Preview */}
                                            <p className={`text-sm text-gray-700 mt-2 ${expandedNotifications.has(notification.id) ? '' : 'line-clamp-2'
                                                }`}>
                                                {notification.message}
                                            </p>

                                            {/* Expanded Content */}
                                            {expandedNotifications.has(notification.id) && notification.data && (
                                                <div className="mt-3 p-3 bg-gray-100 rounded-lg">
                                                    {notification.data.assignmentId && (
                                                        <div className="text-xs text-gray-600">
                                                            <p><strong>ID Tugas:</strong> {notification.data.assignmentId}</p>
                                                            {notification.data.deadline && (
                                                                <p><strong>Deadline:</strong> {notification.data.deadline}</p>
                                                            )}
                                                        </div>
                                                    )}

                                                    {notification.data.actionRequired && (
                                                        <div className="mt-2">
                                                            <button
                                                                onClick={() => handleViewAssignment(notification)}
                                                                className="text-xs bg-blue-500 text-white px-3 py-1 rounded-full hover:bg-blue-600 transition-colors"
                                                            >
                                                                Lihat Detail
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Priority Badge */}
                                            {notification.data?.priority === 'high' && (
                                                <div className="mt-2">
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                        🚨 Prioritas Tinggi
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Footer */}
                    {notifications.length > 0 && (
                        <div className="p-3 border-t border-gray-200 bg-gray-50 text-center">
                            <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                                Lihat Semua Notifikasi
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationCenter;
